import { observer } from 'mobx-react';
import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { PageTopBar } from '~components/page-top-bar';
import { useApplicationSelectionStore } from '~contexts/mobx';
import { AskAIButton } from '~analytics-components/ask-ai-button';
import { SectionHeader } from '~analytics-components/section-header';
import { KpiLoadProgressBar } from '~analytics-components/kpi-load-progress-bar';
import { KpiCardFromSpec } from '~containers/analytics/kpis/kpi-card-from-spec';
import { OverviewToolbar } from '~containers/analytics/overview-page/overview-toolbar';
import { usePageFilter } from '~containers/analytics/overview-page/use-page-filter';
import type { PageFilterQuery } from '~containers/analytics/overview-page/use-page-filter';
import { lastSelectableDay } from '~containers/analytics/kpis/periods';
import type { KpiCardSpec } from '~containers/analytics/kpis/card-spec';
import { DOC31_EDITION_ID } from '~containers/analytics/kpis/sources/doc31-edition';
import { useServedEdition } from '~containers/analytics/use-served-edition';
import { AnalyticsSearchOptions } from '~components/analytics-search-options';
import { useSearchParams } from '~utils/use-search-params';
import {
    OVERALL_PERFORMANCE_CARDS,
    BUSINESS_IMPACT_CARDS,
    GAME_ENGAGEMENT_CARDS,
    COUPON_CARDS,
    COUPON_BREAKDOWN_CARDS,
    ENGAGEMENT_DRIVER_CARDS,
} from './cards';
import style from './style.module.scss';

/**
 * 31DOC dashboard (DOC31-99): the RPP dashboard's sections for one 31DOC
 * edition. The edition and its dates come from `GET /analytics/campaigns`, so
 * the default period is the edition's own window; no picker until a second
 * edition is served.
 */
export const Doc31DashboardPage = observer(() => {
    const applicationSelectionStore = useApplicationSelectionStore();
    const search = useSearchParams<PageFilterQuery>();
    const { edition, campaign, context } = useServedEdition({
        gameKind: '31doc',
        defaultEditionId: DOC31_EDITION_ID,
        gameLabel: '31DOC',
    });
    const {
        filter,
        setPeriod,
        setPreset,
        setRegion,
        setCompare,
    } = usePageFilter({
        search,
        defaultPeriod: 'campaign',
        campaign: campaign &&
            edition && {
                window: campaign,
                campaignId: edition.id,
            },
        region: {
            appTarget: applicationSelectionStore.appTarget,
            defaultProvincesCsv:
                applicationSelectionStore.getProvincesFromSelectedApplication,
        },
    });

    const renderCard = (spec: KpiCardSpec) => (
        <KpiCardFromSpec
            key={spec.title}
            spec={{ ...spec, campaignId: edition?.id }}
            pageRange={filter.range}
            pageRegion={filter.provinces}
            descriptionOnlyInfo
            compareEnabled={filter.compare}
            waveKey={filter.waveKey}
            campaign={context}
        />
    );

    return (
        <>
            <PageTopBar title="31 Days of Circle K Dashboard">
                <AskAIButton />
            </PageTopBar>
            <div className={`${style.dashboard} content_with_margins`}>
                <OverviewToolbar
                    filters={
                        <AnalyticsSearchOptions
                            startDate={filter.range.start}
                            endDate={filter.range.end}
                            updateParams={setPeriod}
                            lastSelectableDay={lastSelectableDay()}
                            periodPresets={{
                                active: filter.preset,
                                onSelect: setPreset,
                                campaign,
                            }}
                            compareEnabled={filter.compare}
                            onCompareChange={setCompare}
                            regionFilter={{
                                value: filter.provinces,
                                disabled: filter.regionLocked,
                                onChange: setRegion,
                            }}
                        />
                    }
                />

                <KpiLoadProgressBar />

                <section className={style.section}>
                    <SectionHeader
                        title="Overall performance"
                        subtitle="players and visits"
                    />
                    <div className={`${style.grid} ${style.grid_3}`}>
                        {OVERALL_PERFORMANCE_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader
                        title="Business impact"
                        subtitle="in-store purchases"
                    />
                    <span
                        className={`${style.section_note} footnote_description`}
                    >
                        <InfoCircleOutlined />
                        Business impact metrics are showing all Canada.
                    </span>
                    <div className={`${style.grid} ${style.grid_3}`}>
                        {BUSINESS_IMPACT_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader
                        title="Game engagement"
                        subtitle="doors opened"
                    />
                    <div className={`${style.grid} ${style.grid_2}`}>
                        {GAME_ENGAGEMENT_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader
                        title="Coupons"
                        subtitle="issued and redeemed"
                    />
                    <div className={`${style.grid} ${style.grid_2}`}>
                        {COUPON_CARDS.map(renderCard)}
                    </div>
                    <div className={`${style.grid} ${style.grid_2}`}>
                        {COUPON_BREAKDOWN_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader
                        title="Engagement drivers"
                        subtitle="badges and bonus actions"
                    />
                    <span
                        className={`${style.section_note} footnote_description`}
                    >
                        <InfoCircleOutlined />
                        Badges and bonus actions are showing all Canada.
                    </span>
                    <div className={`${style.grid} ${style.grid_2}`}>
                        {ENGAGEMENT_DRIVER_CARDS.map(renderCard)}
                    </div>
                </section>
            </div>
        </>
    );
});
